import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Breadcrumb, Button } from 'antd';
import { Tabs } from 'antd';
import _camelCase from 'lodash/camelCase';
import _isEmpty from 'lodash/isEmpty';
import _startCase from 'lodash/startCase';
import _upperFirst from 'lodash/upperFirst';
import PropTypes from 'prop-types';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styled, { css } from 'styled-components';

import DashboardSuggestions from 'components/AiChatBot/DashboardSuggestions';
import { AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY } from 'components/AiChatBot/reducer';
import { FeatureFlag } from 'components/FeatureFlag';
import LocaleSwitcher from 'components/LocaleSwitcher';
import StyledTitle from 'components/Title';
import useGetDetail from 'hooks/useGetDetail';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import i18n from 'utils/i18n';

import { TBreadcrumbs } from './i18n/TModules';

const BreadCrumbs = ({ location, entity_id }) => {
  const { t } = useMultiLanguageTranslation();

  const pathSnippets = useMemo(() => {
    let idOccurrences = [];
    const avoided = ['create', 'show', 'edit', 'insight', 'reward_transactions'];

    const snippets = location.pathname
      .split('/')
      .slice(2)
      .filter(p => !avoided.includes(p))
      .filter(p => {
        // remove all id params except the first for the case of urls
        // like /partners/show/:partnerId/wallet-transaction-history/:walletId
        const isId = /^\d+$/.test(p);

        if (isId) {
          idOccurrences.push(p);
          return idOccurrences.length === 1;
        }

        return true;
      });

    if (location.pathname.endsWith(entity_id) && location.pathname.includes('/edit/')) {
      snippets.push('edit');
    }
    if (location.pathname.endsWith(entity_id) && location.pathname.includes('/insight/')) {
      snippets.push('insight');
    }

    return snippets;
  }, [location.pathname, entity_id]);

  const entity = () => {
    switch (pathSnippets[0]) {
      case 'loyalties':
        return 'loyalty_programs';
      case 'catalogues':
        return 'catalogs';
      case 'rules':
        return 'transaction_rules';
      case 'audiences':
        return 'v4_audiences';
      default:
        return pathSnippets[0];
    }
  };
  const item = useGetDetail(entity_id, entity());

  const breadcrumbItems = pathSnippets.map((path, index) => {
    const label =
      +entity_id === +path
        ? item?.data?.name || item?.data?.title || item?.data?.id || '...'
        : i18n.exists(`${TBreadcrumbs}.${_upperFirst(_camelCase(path))}`)
        ? t(`${TBreadcrumbs}.${_upperFirst(_camelCase(path))}`)
        : _startCase(path);

    const currentPathIsFinal = index === pathSnippets.length - 1;

    const noTransformPaths = [
      'credit-cards',
      'cash-credit',
      'credit_card_redemption_settings',
      'airmiles',
      'hotels',
      'vouchers',
      'mem-fee-waiver'
    ];
    const targetUrl = `${location.pathname.substring(0, location.pathname.indexOf(path))}${path}`;
    const finalUrl = noTransformPaths.includes(path)
      ? targetUrl
      : targetUrl.replace(/(edit|insight)/, 'show');

    return (
      <Breadcrumb.Item key={path}>
        {currentPathIsFinal ? label : <Link to={finalUrl}>{label}</Link>}
      </Breadcrumb.Item>
    );
  });

  return <Breadcrumb style={{ fontSize: '12px' }}>{breadcrumbItems}</Breadcrumb>;
};

interface ITab {
  key: string;
  label: React.ReactNode;
}
interface PageHeaderProps {
  title?: string;
  entity_id?: string | number;
  tabs?: ITab[];
  defaultTab?: string;
  onTabChange?: (p: string) => void;
  renderExtra?;
  primaryAction?;
  showPrimaryAction?;
  customAction?;
  isShowLocaleSwitcher?: boolean;
  disablePrimaryAction?: boolean;
  isSticky?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title = 'Loading...',
  entity_id,
  tabs,
  defaultTab,
  onTabChange = () => {},
  renderExtra,
  primaryAction,
  showPrimaryAction = true,
  customAction,
  isShowLocaleSwitcher = false,
  disablePrimaryAction = false,
  isSticky = false
}) => {
  let location = useLocation();
  if (!location || typeof location.pathname !== 'string') {
    location = { pathname: '/', search: '', state: undefined, hash: '', key: 'default' };
  }
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const isHeaderChipVisible = useSelector((state: any) => state.aiChatBot?.isHeaderChipVisible);
  const open = useSelector((state: any) => state.aiChatBot?.open);

  const formPagePatterns = [/business_intelligence/i];
  let isHeaderQuestionVisible = false;
  if (
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.pathname === 'string'
  ) {
    isHeaderQuestionVisible = formPagePatterns.some(pattern =>
      pattern.test(window.location.pathname)
    );
  }

  // tabs format = [{key: String, label: String}, ...];
  //primaryAction = {type: enum[primary, secondary], label: String, icon: antd icon component, onClick: function}
  const [activeTab, setActiveTab] = useState(
    tabs ? (tabs.find(el => el?.key === defaultTab) ? defaultTab : tabs[0].key) : null
  );
  const subpath = location.pathname.split('/').pop();

  const params = useParams();
  const navigate = useNavigate();

  const changeTab = tab => {
    setActiveTab(tab);
    onTabChange(tab);

    if (location.pathname === '/p/settings') {
      if (tab === 'general' && location.pathname.includes('show')) {
        const splitUrlToArray = location.pathname.split('/');
        const listUrlPath = splitUrlToArray[splitUrlToArray.length - 3];
        navigate(`${location.pathname}/general/${listUrlPath}`);
      }

      if (tab === 'users') {
        navigate(`${location.pathname}/users/individual_users`);
      }
    }
  };

  useEffect(() => {
    if (!_isEmpty(tabs) && tabs.find(el => el?.key === defaultTab)) {
      setActiveTab(defaultTab); // VS-3392
    }
    // eslint-disable-next-line
  }, [tabs, subpath]);

  return (
    <HeaderWrapper isSticky={isSticky}>
      {isHeaderQuestionVisible && (
        <FeatureFlag
          flags={[
            {
              key: 'ai_prototype',
              label: 'Enable eMind',
              subtext: 'Enable the beta eMind UI for AI data analysis.'
            }
          ]}
        >
          <DashboardSuggestions
            questions={[
              'How many loyalty members redeemed rewards this month?',
              'Which rewards are driving the most engagement?',
              'How much did I spend on Rewards this quarter?',
              'Which campaign gave me the best ROI based on rewards cost?',
              "What's my cost per engagement or action?"
            ]}
          />
        </FeatureFlag>
      )}

      <HeaderContainer tabs={!!tabs} data-testid="page-header">
        <Helmet titleTemplate={'Perx | %s'}>
          <title>{title}</title>
        </Helmet>
        <TopWrapper>
          <TopMain>
            <BreadCrumbs {...{ location, entity_id }} />
            <StyledTitle>{title}</StyledTitle>
            {entity_id && (
              <>
                <div>
                  ID: <span className={'text-muted'}>{entity_id}</span>
                </div>
              </>
            )}
          </TopMain>
          <TopActions>
            <ActionsBtnGroup>
              {customAction && typeof customAction === 'function' ? customAction() : <> </>}
              {showPrimaryAction && primaryAction && (
                <div style={{ textAlign: 'right' }}>
                  <Button
                    size="large"
                    type={primaryAction.type ?? 'primary'}
                    icon={primaryAction?.icon}
                    onClick={primaryAction?.onClick}
                    {...primaryAction?.props}
                    disabled={disablePrimaryAction}
                  >
                    {primaryAction?.label}
                  </Button>
                </div>
              )}
            </ActionsBtnGroup>
            {isShowLocaleSwitcher && (
              <div style={{ marginTop: primaryAction ? '20px' : '0' }}>
                <LocaleSwitcher />
              </div>
            )}
          </TopActions>
        </TopWrapper>
        {typeof renderExtra === 'function' && <ExtraContainer>{renderExtra()}</ExtraContainer>}
        {tabs && Array.isArray(tabs) && (
          <Tabs
            defaultActiveKey={tabs[0]?.key}
            activeKey={activeTab}
            onTabClick={changeTab}
            tabBarStyle={{ marginBottom: 0, marginTop: '1rem' }}
            items={tabs}
          />
        )}

        {isHeaderChipVisible && isHeaderQuestionVisible && !open && (
          <FeatureFlag
            flags={[
              {
                key: 'ai_prototype',
                label: 'Enable eMind',
                subtext: 'Enable the beta eMind UI for AI data analysis.'
              }
            ]}
          >
            <AiChatChip
              onClick={() => {
                dispatch({ type: AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY, payload: false });
              }}
              onMouseEnter={() => {
                dispatch({ type: AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY, payload: false });
              }}
            >
              <svg
                width="20"
                height="19"
                viewBox="0 0 20 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.02692 13.5117C7.22829 13.645 7.46891 13.7164 7.71543 13.7158C7.96195 13.7152 8.20217 13.6428 8.40284 13.5085C8.60626 13.3692 8.76053 13.1763 8.8451 12.9534L9.35593 11.4822C9.48701 11.1135 9.70774 10.7783 10.0007 10.5033C10.2936 10.2282 10.6507 10.0208 11.0438 9.89741L12.6335 9.41522C12.8685 9.33544 13.0709 9.18852 13.2121 8.99517C13.3534 8.80181 13.4264 8.57178 13.4208 8.33747C13.4153 8.10316 13.3315 7.87639 13.1812 7.68911C13.0309 7.50183 12.8217 7.36349 12.5832 7.29357L11.0118 6.81353C10.6183 6.69076 10.2607 6.48386 9.96715 6.20918C9.67362 5.9345 9.45223 5.59957 9.3205 5.23086L8.80396 3.74357C8.72078 3.52445 8.56684 3.33509 8.36355 3.20183C8.16025 3.06857 7.91772 2.99804 7.66968 3.00004C7.42164 3.00205 7.18044 3.07649 6.97962 3.21301C6.77881 3.34954 6.62837 3.54136 6.54923 3.76178L6.02697 5.26194C5.89587 5.62076 5.67966 5.94702 5.39461 6.21615C5.10957 6.48527 4.76312 6.69026 4.38135 6.81567L2.79287 7.29465C2.55928 7.3728 2.35744 7.51721 2.21536 7.70783C2.07329 7.89844 1.99803 8.1258 2.00004 8.35835C2.00205 8.5909 2.08123 8.81708 2.22658 9.00552C2.37193 9.19395 2.57625 9.33527 2.81116 9.40986L4.38135 9.8867C4.77684 10.0098 5.13601 10.2181 5.42996 10.4951C5.72392 10.772 5.94445 11.1097 6.07383 11.4811L6.59037 12.9717C6.67265 13.1903 6.82579 13.3788 7.02692 13.5117Z"
                  fill="url(#paint0_linear_535_11449)"
                />
                <path
                  d="M14.0402 17.8425C14.1571 17.9195 14.2915 17.97 14.4328 17.99C14.5741 18.0099 14.7185 17.9988 14.8544 17.9574C14.9903 17.916 15.1141 17.8456 15.2159 17.7516C15.3178 17.6576 15.3949 17.5427 15.4413 17.416L15.7247 16.5995C15.7861 16.4303 15.8877 16.2764 16.0218 16.1494C16.1567 16.0209 16.3212 15.9266 16.5018 15.8708L17.384 15.6008C17.5661 15.5419 17.7236 15.4306 17.8342 15.2827C17.9448 15.1349 18.0028 14.9581 17.9999 14.7775C17.997 14.597 17.9333 14.4219 17.818 14.2772C17.7027 14.1326 17.5416 14.0258 17.3577 13.9721L16.4847 13.7042C16.3041 13.6475 16.1401 13.5524 16.0053 13.4264C15.8704 13.3003 15.7686 13.1468 15.7076 12.9777L15.4196 12.1494C15.3577 11.979 15.2403 11.8312 15.0839 11.727C14.9274 11.6227 14.74 11.5672 14.5481 11.5684C14.3562 11.5696 14.1695 11.6274 14.0146 11.7336C13.8596 11.8398 13.7443 11.989 13.6848 12.1601L13.4025 12.9766C13.3442 13.1446 13.2453 13.2976 13.1134 13.4239C12.9816 13.5501 12.8205 13.646 12.6426 13.7042L11.7592 13.9742C11.5774 14.032 11.4197 14.1419 11.3083 14.2884C11.1968 14.435 11.1374 14.6106 11.1384 14.7906C11.1395 14.9706 11.2008 15.1456 11.3139 15.2911C11.4269 15.4365 11.5859 15.5448 11.7683 15.6008L12.6403 15.8655C12.8231 15.9233 12.9877 16.0187 13.1226 16.1451C13.2586 16.2727 13.3591 16.427 13.4174 16.5973L13.7065 17.4267C13.7691 17.5944 13.8857 17.7396 14.0402 17.8425Z"
                  fill="url(#paint1_linear_535_11449)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_535_11449"
                    x1="0.937332"
                    y1="3.7192"
                    x2="9.51105"
                    y2="14.9916"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0.4" stopColor="#8B5CF6" />
                    <stop offset="1" stopColor="#469BFF" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_535_11449"
                    x1="10.5"
                    y1="12"
                    x2="15"
                    y2="18"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0.3" stopColor="#8B5CF6" />
                    <stop offset="1" stopColor="#469BFF" />
                  </linearGradient>
                </defs>
              </svg>
              Need insights? Lets talk
            </AiChatChip>
          </FeatureFlag>
        )}
      </HeaderContainer>
    </HeaderWrapper>
  );
};

PageHeader.propTypes = {
  // @ts-ignore for some reason there is some incompatibility with the props defined above
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node
    })
  ),
  onTabChange: PropTypes.func,
  defaultTab: PropTypes.string,
  title: PropTypes.string,
  entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  primaryAction: PropTypes.shape({
    type: PropTypes.oneOf([
      'link',
      'primary',
      'danger',
      'default',
      'disabled',
      'lime',
      'draft',
      'active',
      'pending',
      'approved',
      'ended',
      'inactive'
    ]),
    label: PropTypes.string,
    icon: PropTypes.object,
    onClick: PropTypes.func
  }),
  renderExtra: PropTypes.func,
  customAction: PropTypes.func,
  isShowLocaleSwitcher: PropTypes.bool
  // selectedLanguage: PropTypes.string,
  // setSelectedLanguage: PropTypes.func
};

export default PageHeader;

const HeaderWrapper = styled.div<{
  isSticky?: boolean;
}>`
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  ${({ isSticky }) =>
    isSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 100;
    `}
`;
const HeaderContainer = styled.div<{
  tabs?: boolean;
}>`
  padding: ${props => (props.tabs ? '2rem 2rem 0' : '2rem')};
`;

const AiChatChip = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6.5px 10px;
  font-family: 'Inter', sans-serif;
  position: absolute;
  top: 0;
  right: 50%;
  transform: translate(50%, 0%);
  background: linear-gradient(90deg, #ede9fe 0%, #d6eff9 100%);
  border: none;
  border-radius: 0px 0px 8px 8px;
  outline: none;
  cursor: pointer;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0px;
  color: #2665ee;
  z-index: 1;
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -8px;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #ede9fe;
  }
`;

const TopWrapper = styled.div`
  display: flex;
`;

const TopMain = styled.div`
  flex: 1;
`;

const ActionsBtnGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex-direction: row;
  justify-content: flex-end;
`;

const TopActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const ExtraContainer = styled.div`
  margin-top: 1rem;
`;
